import React, { memo, useMemo } from 'react'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Card, Col, Row } from 'react-bootstrap';
import UserItem from '../common/UserItem';
import { datasourceOptions, formatDate, setObjectType } from '../../utils';
import { useAuth } from '../../contexts/auth';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Datasource, MessageBrokerDatasource, ObjectStorageDatasource } from '../../models/Datasource';
import { Tooltip } from 'antd';

const datasourceIcons = datasourceOptions.reduce((acc, curr) => {
  acc[curr.value] = curr.icon;
  return acc;
}, {} as Record<string, IconDefinition>);

type Props = {
  datasource: Datasource;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  onEdit: React.MouseEventHandler<HTMLButtonElement>;
  onDelete: React.MouseEventHandler<HTMLButtonElement>;
  onTest: React.MouseEventHandler<HTMLButtonElement>;
}

function DatasourceItem({ datasource, onClick, onEdit, onDelete, onTest }: Props) {
  const { loggedUser } = useAuth();
  const date = formatDate(datasource.createDateTime as string);
  const icon = datasourceIcons[datasource.type] || solid('faucet-drip');
  const showActions = useMemo(() => {
    return (datasource.accessLevel === "TEAM" && datasource.organization!.id === loggedUser?.organizationId)
      || (datasource.accessLevel === "PRIVATE" && datasource.owner!.id === loggedUser?.sub);
  }, [datasource, loggedUser]);

  const renderPills = () => {
    if (datasource.type === "messageBroker" && setObjectType<MessageBrokerDatasource>(datasource)) {
      return (
        <Tooltip title="Brokers">
          <div className="list-item-pill d-inline-block text-truncate">
            <FontAwesomeIcon icon={solid('message')} />
            <span className="ms-2">{datasource.brokers}</span>
          </div>
        </Tooltip>
      )
    }
    if (datasource.type === "database" || datasource.type === "fileSystem") return (
      <Tooltip title="Host">
        <div className="list-item-pill d-inline-block text-truncate">
          <FontAwesomeIcon icon={solid('computer')} />
          <span className="ms-2">{datasource.host}</span>
        </div>
      </Tooltip>
    )
    else return setObjectType<ObjectStorageDatasource>(datasource) && (
      <Tooltip title="Bucket">
        <div className="list-item-pill d-inline-block text-truncate">
          <FontAwesomeIcon icon={solid('bucket')} />
          <span className="ms-2">{datasource.bucket}</span>
        </div>
      </Tooltip>
    );
  }

  return (
    <Card className="app-list-item" onClick={onClick}>
      <Card.Header className="list-item-header bg-datasource">
        <div className="list-item-icon">
          <FontAwesomeIcon icon={icon} />
        </div>

        <Row className="gx-0 align-items-start">
          <Col className="list-item-date">
            <b>Created</b>&nbsp;
            <span>{date}</span>
          </Col>

          <Badge className="shadow w-auto">{datasource.accessLevel}</Badge>
        </Row>

        <div className="header-content">
          <Tooltip title={datasource.name} mouseEnterDelay={0.3}>
            <h5 className="list-item-title">{datasource.name}</h5>
          </Tooltip>
          {/* <Badge bg="none" className={classNames('asset-mode', 'shadow', modeClass)} pill={true}>
            <FontAwesomeIcon icon={modeIcon} />
            <span className="ms-2">{asset.workflowMode}</span>
          </Badge> */}
          <p className="list-item-description line-clamp-2 mt-2">{datasource.description}</p>
        </div>

        <Row className="gx-0 justify-content-center">
          <Col className="text-center" xs={8}>
            <Tooltip title="Test Connection" mouseEnterDelay={0.3}>
              <button className="circle-button ms-2" onClick={onTest}>
                <FontAwesomeIcon icon={solid("signal")} />
              </button>
            </Tooltip>
            {showActions && (
              <>
                <Tooltip title="Edit" mouseEnterDelay={0.3}>
                  <button className="circle-button ms-2" onClick={onEdit}>
                    <FontAwesomeIcon size="sm" icon={solid('pen')} />
                  </button>
                </Tooltip>
                <Tooltip title="Delete" mouseEnterDelay={0.3}>
                  <button className="circle-button ms-2" onClick={onDelete}>
                    <FontAwesomeIcon size="sm" icon={solid('trash')} />
                  </button>
                </Tooltip>
              </>
            )}
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="p-2 position-relative">
        <UserItem user={datasource.owner!} organization={datasource.organization!} className="mb-2" />

        <div className="text-center">{renderPills()}</div>
      </Card.Body>
    </Card >
  )
}

export default memo(DatasourceItem);
